// GraphQL operation strings for the People Management panel. Co-located per the
// clinic client convention (see src/hooks/useClinicDoctors.js / screens/*/constants.js).

const ROLE_FIELDS = `
  id roleKey nameFa description color permissions memberCount syncStatus
`;

const MEMBER_FIELDS = `
  id userId displayName phone tier status joinedAt syncStatus
  roles { id nameFa color }
  extraPermissions effectivePermissions
`;

export const PERMISSION_CATALOG_QUERY = `
  query PermissionCatalog {
    permissionCatalog { key module moduleFa labelFa descriptionFa }
    roleTemplates { key nameFa permissions }
  }
`;

export const LIST_ROLES_QUERY = `
  query ListClinicRoles { listClinicRoles { ${ROLE_FIELDS} } }
`;

export const LIST_MEMBERS_QUERY = `
  query ListStaffMembers($search: String, $roleId: ID, $status: StaffStatus) {
    listStaffMembers(search: $search, roleId: $roleId, status: $status) { ${MEMBER_FIELDS} }
  }
`;

export const SYNC_STATUS_QUERY = `
  query PeopleSyncStatus { peopleSyncStatus { inSync issues { kind target detail } } }
`;

export const CREATE_ROLE_MUTATION = `
  mutation CreateClinicRole($input: ClinicRoleInput!) {
    createClinicRole(input: $input) { ${ROLE_FIELDS} }
  }
`;

export const UPDATE_ROLE_MUTATION = `
  mutation UpdateClinicRole($id: ID!, $input: ClinicRoleInput!) {
    updateClinicRole(id: $id, input: $input) { ${ROLE_FIELDS} }
  }
`;

export const DELETE_ROLE_MUTATION = `
  mutation DeleteClinicRole($id: ID!, $reassignToRoleId: ID) {
    deleteClinicRole(id: $id, reassignToRoleId: $reassignToRoleId)
  }
`;

export const ADD_MEMBER_MUTATION = `
  mutation AddStaffMember($input: AddStaffInput!) {
    addStaffMember(input: $input) { ${MEMBER_FIELDS} }
  }
`;

export const SET_MEMBER_ROLES_MUTATION = `
  mutation SetStaffRoles($staffId: ID!, $roleIds: [ID!]!, $extraPermissions: [String!]) {
    setStaffRoles(staffId: $staffId, roleIds: $roleIds, extraPermissions: $extraPermissions) { ${MEMBER_FIELDS} }
  }
`;

export const SET_MEMBER_TIER_MUTATION = `
  mutation SetStaffTier($staffId: ID!, $tier: StaffTier!) {
    setStaffTier(staffId: $staffId, tier: $tier) { ${MEMBER_FIELDS} }
  }
`;

export const SUSPEND_MEMBER_MUTATION = `
  mutation SuspendStaffMember($staffId: ID!) { suspendStaffMember(staffId: $staffId) { ${MEMBER_FIELDS} } }
`;

export const ACTIVATE_MEMBER_MUTATION = `
  mutation ActivateStaffMember($staffId: ID!) { activateStaffMember(staffId: $staffId) { ${MEMBER_FIELDS} } }
`;

export const REMOVE_MEMBER_MUTATION = `
  mutation RemoveStaffMember($staffId: ID!) { removeStaffMember(staffId: $staffId) }
`;

export const REPAIR_SYNC_MUTATION = `
  mutation RepairPeopleSync { repairPeopleSync { inSync issues { kind target detail } } }
`;

export const PENDING_REQUESTS_QUERY = `
  query PendingJoinRequests {
    pendingJoinRequests { id userId displayName phone status requestedAt }
  }
`;

export const APPROVE_REQUEST_MUTATION = `
  mutation ApproveJoinRequest($requestId: ID!, $tier: StaffTier!, $roleIds: [ID!]!) {
    approveJoinRequest(requestId: $requestId, tier: $tier, roleIds: $roleIds) { id }
  }
`;

export const REJECT_REQUEST_MUTATION = `
  mutation RejectJoinRequest($requestId: ID!) { rejectJoinRequest(requestId: $requestId) }
`;

// Tier display labels (Persian) keyed by the GraphQL StaffTier enum.
export const TIER_LABELS = (t) => ({ OWNER: t('people.tiers.owner'), ADMIN: t('people.tiers.admin'), MEMBER: t('people.tiers.member') });
